import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { useSwipeToDismiss, type SwipeDirection } from '@/hooks/useSwipeToDismiss'
import { shouldSuppressAutoFocus } from "@/hooks/use-mobile"

// Context to pass close function to SheetContent for swipe-to-dismiss
const SheetCloseContext = React.createContext<(() => void) | null>(null)

function Sheet({ onOpenChange, ...props }: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) {
  const handleClose = React.useCallback(() => {
    onOpenChange?.(false)
  }, [onOpenChange])

  return (
    <SheetCloseContext.Provider value={handleClose}>
      <SheetPrimitive.Root onOpenChange={onOpenChange} {...props} />
    </SheetCloseContext.Provider>
  )
}
Sheet.displayName = "Sheet"

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 overlay-animated",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background shadow-lg",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b sheet-animated",
        // UX mobile : padding horizontal + safe-area pour ne pas coller aux
        // bords physiques (encoche / home indicator latéral en paysage).
        // Coins arrondis haut pour un look modal-native iOS / Android.
        // Consommateurs avec leur propre padding (p-0, p-4) le ré-écrasent
        // naturellement via tailwind-merge.
        bottom:
          "inset-x-0 bottom-0 border-t sheet-animated rounded-t-2xl " +
          "pt-6 pb-[calc(1rem+env(safe-area-inset-bottom))] " +
          "pl-[calc(1rem+env(safe-area-inset-left))] " +
          "pr-[calc(1rem+env(safe-area-inset-right))]",
        left: "inset-y-0 left-0 h-full w-3/4 border-r sheet-animated sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 sm:max-w-sm sheet-animated",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  /**
   * `false` : ni le contenu ni le voile ne se referment au glisser, et la poignée — qui
   * annonce ce geste — disparaît. ⛔ À poser sur un volet qui porte un FORMULAIRE : tirer
   * vers le bas en faisant défiler un long formulaire le refermait, brouillon perdu
   * (36_CUSTOM, réglage des champs sur téléphone, 2026-09-22). La croix reste.
   */
  swipeToDismiss?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, swipeToDismiss = true, ...props }, ref) => {
  const { t } = useTranslation()
  const closeFromContext = React.useContext(SheetCloseContext)

  // ⚠️ Le geste de fermeture n'a de sens que sur un volet LATÉRAL.
  //
  // Un volet ancré en haut ou en bas ENTRE et SORT verticalement (cf. l'animation
  // `.sheet-animated[data-side="bottom"]` d'index.css) ; le renvoyer vers la
  // droite est à contresens, et surtout : tout glissement horizontal de 60 px
  // À L'INTÉRIEUR du volet le referme — y compris en posant le curseur dans un
  // champ de filtre. On neutralise donc le geste plutôt que de le retourner.
  //
  // Le câbler verticalement suppose d'abord une garde de défilement dans
  // `useSwipeToDismiss` : son `onTouchStart` ne lit JAMAIS le `scrollTop`, donc
  // un renvoi vers le bas fermerait le volet dès qu'on remonte une liste —
  // régression pire que le défaut. Arbitrage laissé au PO.
  // Chaque volet se referme dans la direction par laquelle il est SORTI : un
  // volet du bas se tire vers le bas, un volet latéral se pousse vers son bord.
  // Le renvoyer à contresens — ce que faisait la version précédente pour les
  // volets haut/bas — refermait le panneau au moindre glissement horizontal,
  // jusqu'à poser le curseur dans un champ de filtre.
  //
  // Le geste VERTICAL n'est sûr que depuis la garde de défilement ajoutée à
  // `useSwipeToDismiss` : sans elle, remonter la liste des colonnes refermait le
  // volet, le doigt allant vers le bas dans les deux cas.
  const swipeDirection: SwipeDirection =
    side === "bottom" ? "down" : side === "top" ? "up" : side === "left" ? "left" : "right"
  const swipe = useSwipeToDismiss({
    onDismiss: closeFromContext ?? (() => {}),
    direction: swipeDirection,
  })

  // Merge the forwarded ref with the swipe ref
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      // Set swipe ref
      (swipe.ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      // Forward ref
      if (typeof ref === 'function') ref(node)
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
    },
    [ref, swipe.ref]
  )

  // `swipeToDismiss={false}` : AUCUN écouteur — ni sur le voile, ni sur le contenu.
  const geste = swipeToDismiss
    ? { onTouchStart: swipe.onTouchStart, onTouchMove: swipe.onTouchMove, onTouchEnd: swipe.onTouchEnd }
    : {}

  return (
    <SheetPortal>
      <SheetOverlay {...geste} />
      <SheetPrimitive.Content
        ref={mergedRef}
        className={cn(sheetVariants({ side }), className)}
        data-side={side}
        // TECH-011 — Mobile/tactile : pas d'auto-focus à l'ouverture du tiroir
        // (évite l'ouverture intempestive du clavier virtuel). Surchargeable.
        onOpenAutoFocus={(event) => {
          if (shouldSuppressAutoFocus()) event.preventDefault()
        }}
        {...geste}
        {...props}
      >
        {/* Poignée : un geste qui n'est annoncé par rien n'est jamais découvert
            (règle « un geste ne doit jamais être la seule façon d'accéder à une
            action » — la croix et le bouton « Fermer » restent, celle-ci dit
            seulement qu'on peut aussi tirer). */}
        {side === "bottom" && swipeToDismiss && (
          <div className="mx-auto -mt-3 mb-2 h-1 w-10 shrink-0 rounded-full bg-border" aria-hidden />
        )}
        {children}
        <SheetPrimitive.Close
          className={cn(
            "absolute right-[calc(1rem+env(safe-area-inset-right))] h-10 w-10 flex items-center justify-center",
            "text-muted-foreground opacity-80 hover:opacity-100 hover:text-foreground transition-opacity z-10",
            // `focus:outline-none` était posé SANS anneau de remplacement, et
            // aucune règle `:focus-visible` globale ne le rattrape : la croix
            // était invisible au clavier.
            "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            // ⚠️ Un volet ancré EN BAS ne touche jamais le bord HAUT du viewport.
            // `env(safe-area-inset-top)` y renvoie quand même la valeur globale de
            // l'appareil — 44 à 59 px en PWA installée — et poussait la croix
            // 60 à 75 px À L'INTÉRIEUR du volet, par-dessus son contenu. Sur le
            // volet « Trier / Filtrer », elle atterrissait sur le premier bouton
            // de tri. Invisible en onglet Safari, où l'inset vaut 0 : ne se
            // reproduit qu'en PWA INSTALLÉE.
            side === "bottom" ? "top-2" : "top-[calc(1rem+env(safe-area-inset-top))]",
          )}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">{t("common.close")}</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
